import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
  nofollow?: boolean;
  canonical?: string;
  structuredData?: any;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  noindex = false,
  nofollow = false,
  canonical,
  structuredData,
}) => {
  const { t, i18n } = useTranslation();
  
  // Default values
  const defaultTitle = t('seo.defaultTitle', 'Band Space - Sale Prób i Studia Muzyczne');
  const defaultDescription = t('seo.defaultDescription', 'Band Space - Najlepsza platforma do znajdowania i rezerwowania sal prób oraz studiów nagraniowych.');
  const defaultKeywords = t('seo.defaultKeywords', 'sala prób, band space, przestrzeń muzyczna, studio nagrań, pokój prób, wynajem sali prób, music rehearsal space, recording studio, practice room, band practice, music studio, rehearsal room, music venue, band practice rooms');
  const defaultImage = '/logo.svg'; // Your app's default image
  // Get the current domain and ensure we use the canonical version (non-www)
  const getCanonicalUrl = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    
    // Always use non-www version as canonical
    if (url.hostname.startsWith('www.')) {
      url.hostname = url.hostname.replace('www.', '');
    }
    
    return url.toString();
  };
  
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://band-space.com'; // Use your actual domain
  
  // Use provided values or fall back to defaults
  // If title already contains "Band Space", don't concatenate with default title
  const finalTitle = title ? 
    (title.includes('Band Space') ? title : `${title} | ${defaultTitle}`) : 
    defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = image ? (image.startsWith('http') ? image : `${baseUrl}${image}`) : `${baseUrl}${defaultImage}`;
  const finalUrl = url ? (url.startsWith('http') ? url : `${baseUrl}${url}`) : baseUrl;
  const finalCanonical = canonical ? (canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`) : getCanonicalUrl();

  // Robots meta tag
  const robots = [];
  if (noindex) robots.push('noindex');
  if (nofollow) robots.push('nofollow');
  if (!noindex && !nofollow) robots.push('index, follow');
  
  const robotsContent = robots.join(', ');

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "MusicVenue",
    "name": "Band Space",
    "description": finalDescription,
    "url": baseUrl,
    "logo": `${baseUrl}/logo.svg`,
    "image": finalImage,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "PL"
    },
    "sameAs": [],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/user/search`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={finalCanonical} />
      
      {/* Language and Locale */}
      <html lang={i18n.language} />
      <meta property="og:locale" content={i18n.language} />
      <meta property="og:locale:alternate" content="en" />
      <meta property="og:locale:alternate" content="pl" />
      <meta property="og:locale:alternate" content="ru" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content="Band Space" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      
      {/* Additional Open Graph Tags for Articles */}
      {type === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* App-specific Meta Tags */}
      <meta name="application-name" content="Band Space" />
      <meta name="theme-color" content="#1976d2" />
      <meta name="msapplication-TileColor" content="#1976d2" />
      
      {/* Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 